import { LitElement, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import imageIcon from '@amityeko/design-system/dist/assets/icons/image';

import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/inline-message';
import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';

import '../image-layout-content';
import type RnRImageLayoutContent from '../image-layout-content';
import { LayoutList } from '../image-layout-list/types';

import { ImageData, ImageKey } from './types';

import styles from './styles';

/**
 * @dependency amityeko-dialog
 * @dependency amityeko-inline-message
 * @dependency amityeko-icon
 * @dependency rnr-image-layout-content
 */
@customElement('rnr-image-layout')
export default class RnRImageLayout extends withAuth(LitElement) {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;
  @query('#image_layoout_content') imageLayoutContent!: RnRImageLayoutContent;

  @state()
  currentLayout: LayoutList = LayoutList.SQUARE;

  @state()
  imageCount: number = 1;

  @state()
  imageData: ImageData[] = [];

  @state()
  imageKeys: ImageKey[] = [];

  @state()
  isConfimrBtnDisabled?: boolean = true;

  public show(selectedLayout: LayoutList) {
    if (!selectedLayout) return;
    this.currentLayout = selectedLayout;

    this._getImageCount();
    this.dialog.show();
  }

  public reset() {
    this.currentLayout = LayoutList.SQUARE;
    this.imageCount = 1;
    this.imageData = [];
    this.imageKeys = [];
    this.isConfimrBtnDisabled = true;
  }

  private _onClick() {
    this.dialog.show();
    this._getImageCount();
  }

  private async _uploadImages(imageData: any) {
    try {
      const formData = new FormData();
      for (const data of imageData) {
        const fileName = data.file.name;
        formData.append('file', data.file, fileName);
      }

      const response = await this.auth?.http?.upload('/api/v1/media/images', formData);
      this.imageKeys = response?.map((data: any) => ({ type: 'image', key: data.key }));
    } catch (e: any) {
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error.message, variant: 'error' },
      });
    } finally {
      emit(this, 'rnr-confirm-image-layout', {
        detail: {
          layout: this.currentLayout,
          imageCount: this.imageCount,
          imageKeys: this.imageKeys,
        },
      });
      this.reset();
      this.imageLayoutContent.reset();
    }
  }

  private _getImageCount() {
    switch (this.currentLayout) {
      case 'square':
      case 'circle':
        this.imageCount = 1;
        break;
      case 'square-3h':
      case 'square-1h2s':
        this.imageCount = 3;
        break;
      case 'square-4s':
      case 'square-1v3s':
        this.imageCount = 4;
        break;
      default:
        break;
    }
  }

  private _handleFileChange(e: CustomEvent) {
    this.imageData = e?.detail?.imageData;

    if (this.imageData.length === this.imageCount) {
      this.isConfimrBtnDisabled = e?.detail?.isConfimrBtnDisabled;
    }
  }

  private _handleConfirmDialog() {
    this._uploadImages(this.imageData);
    this.dialog.hide();
    emit(this, 'rnr-confirm-image-layout');
  }

  private _onDialogBack() {
    this.dialog.hide();
    emit(this, 'rnr-image-layout-back');
  }

  protected render() {
    return html`
      <slot @click=${this._onClick}></slot>

      <amityeko-dialog
        id="dialog"
        label="Choose Image"
        .confirmButtonText=${'Done'}
        ?isDisabled=${this.isConfimrBtnDisabled}
        ?hasBackButton=${true}
        @amityeko-back=${this._onDialogBack}
        @amityeko-confirm=${this._handleConfirmDialog}
      >
        <div class="content">
          <amityeko-inline-message .variant=${'info'} ?hasCustomIcon=${true}>
            <amityeko-icon .iconSize=${'20px'} slot="custom-icon">${imageIcon}</amityeko-icon>
            Please choose ${this.imageCount} ${this.imageCount === 1 ? 'image' : 'images'} for this
            layout
          </amityeko-inline-message>
          <rnr-image-layout-content
            id="image_layoout_content"
            .imageCount=${this.imageCount}
            .currentLayout=${this.currentLayout}
            @rnr-change-file=${this._handleFileChange}
          >
          </rnr-image-layout-content>
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-image-layout': RnRImageLayout;
  }
}
